/* eslint-disable arrow-body-style */
import { mapActions, mapGetters, mapState } from 'vuex';
import ls from '@/helpers/localStorage';
import { toCents } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import Contract from '@/models/contract';
import { MODALS } from '@/components/app/app-modals/modals-constants';

const PRICE_LINKS_NOTICE = 'Tap price links for order priority';

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    scrollable: {
      type: Boolean,
      default: false,
    },
    minimumRows: {
      type: Number,
      default: 0,
    },
    contract: {
      type: Object,
      required: true,
    },
    orderBook: {
      type: Object,
    },
    buyOffers: {
      type: Array,
      default: [],
    },
    sellOffers: {
      type: Array,
      default: [],
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    cardLayout: {
      type: Boolean,
      default: false,
    },
    compactCardLayout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAll: this.scrollable,
      showAllCut: 5,
      hoveredIndex: null,
      leftPricesUnavailable: true,
      rightPricesUnavailable: true,
      sideActive: 'left',
    };
  },
  created() {
    this.updateShowAll();
  },
  watch: {
    'contract.contractId': {
      handler() {
        this.updateShowAll();
      },
    },
    orderBook() {
      this.leftPricesUnavailable = true;
      this.rightPricesUnavailable = true;
    },
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapState('trade', ['tradeOptions']),
    isHoverable() {
      if (!this.contractModel.mode) return true;
      if (this.contractModel.mode === this.tradeOptions.operationValue) return true;
      return this.contractModel.userOpenOrders === 0 && this.contractModel.userQuantity === 0;
    },
    contractModel() {
      return new Contract(this.contract);
    },
    classModifiers() {
      return {
        'order-book-prices-sushi': true,
        'order-book-prices-sushi--card-layout': this.cardLayout,
        'order-book-prices-sushi--compact-card': this.cardLayout && this.compactCardLayout,
        'order-book-prices-sushi--card-empty': this.isCardEmpty,
        'order-book-prices-sushi--scrollable-empty': this.isScrollableEmpty,
      };
    },
    tableModifiers() {
      return {
        'order-book-sushi__table': true,
        'order-book-sushi__table--scrollable': this.scrollable,
      };
    },
    usesStructuredRows() {
      return this.cardLayout || this.scrollable || this.minimumRows > 0;
    },
    bodyModifiers() {
      return {
        'order-book-sushi__body': true,
        'order-book-sushi__body--scrollable': this.scrollable,
      };
    },
    showInlineUnavailableSides() {
      return this.isLayoutMobile && !this.scrollable && !this.usesStructuredRows;
    },
    leftSideNoOffersModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__unavailable': true,
        'order-book-sushi__unavailable--active': (this.sideActive === 'left'),
      };
    },
    rightSideNoOffersModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__unavailable': true,
        'order-book-sushi__unavailable--right': true,
        'order-book-sushi__unavailable--active': (this.sideActive === 'right'),
      };
    },
    leftSideHeaderModifiers() {
      return {
        'order-book-sushi__side': !this.usesStructuredRows,
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': (this.sideActive === 'left'),
      };
    },
    rightSideHeaderModifiers() {
      return {
        'order-book-sushi__side': !this.usesStructuredRows,
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': (this.sideActive === 'right'),
      };
    },
    isSharesOwned() {
      return this.contract.userQuantity > 0 || this.buyOffers.length > 0;
    },
    operationValueText() {
      return this.operationValue.charAt(0).toUpperCase() + this.operationValue.slice(1);
    },
    operationTypeLeftText() {
      return 'Buy';
    },
    operationTypeRightText() {
      return 'Sell';
    },
    operationValueRightText() {
      // const opposite = this.operationValue === 'yes' ? 'No' : 'Yes';
      return this.operationValueText;
    },
    pricesGrouped() {
      const prices = [];
      const yesOrders = this.orderBook ? this.orderBook.yesOrders || [] : [];
      const noOrders = this.orderBook ? this.orderBook.noOrders || [] : [];

      let leftPrices = [...yesOrders];
      let rightPrices = [...noOrders];
      let leftField = 'pricePerShare';
      let rightField = 'costPerShareYes';
      let leftOffers = this.sellOffers;
      let rightOffers = this.buyOffers;
      this.sideActive = 'left';

      if (this.isSharesOwned) {
        if ((this.operationValue === 'yes' && this.operationType === 'buy') || (this.operationType === 'sell' && this.operationValue === 'no')) {
          leftPrices = [...yesOrders];
          rightPrices = [...noOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareYes';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...noOrders];
            rightPrices = [...yesOrders];
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
            leftField = 'pricePerShare';
            rightField = 'costPerShareNo';
          }
        } else {
          leftPrices = [...noOrders];
          rightPrices = [...yesOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareNo';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...yesOrders];
            rightPrices = [...noOrders];
            leftField = 'pricePerShare';
            rightField = 'costPerShareYes';
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
          }
        }
      } else if (this.operationValue === 'no') {
        rightField = 'costPerShareNo';
        leftPrices = [...noOrders];
        rightPrices = [...yesOrders];
      }

      const leftOffersField = leftField;
      const rightOffersField = rightField;

      const max = leftPrices.length > rightPrices.length ? leftPrices.length : rightPrices.length;

      for (let i = 0; i < max; i += 1) {
        if (leftPrices[i]) {
          this.leftPricesUnavailable = false;
        }
        if (rightPrices[i]) {
          this.rightPricesUnavailable = false;
        }

        prices.push({
          index: i,
          buyPrice: leftPrices[i] ? toCents(leftPrices[i][leftField]) : null,
          buyQuantity: leftPrices[i] ? leftPrices[i].quantity : null,
          buyOwned: leftPrices[i] ?
            leftOffers.find(s => s.pricePerShare === leftPrices[i][leftOffersField])
            !== undefined : false,
          sellPrice: rightPrices[i] ? toCents(rightPrices[i][rightField]) : null,
          sellQuantity: rightPrices[i] ? rightPrices[i].quantity : null,
          sellOwned: rightPrices[i] ?
            rightOffers.find(s => s.pricePerShare === rightPrices[i][rightOffersField])
              !== undefined : false,
        });
      }
      return prices;
    },
    pricesOwned() {
      return this.pricesGrouped.find(item => item.buyOwned || item.sellOwned);
    },
    showOwnedIndicators() {
      return !!this.pricesOwned;
    },
    showOwnedTrayNotice() {
      return this.scrollable && this.showOwnedIndicators;
    },
    showPriceLinksNotice() {
      return !!this.pricesOwned && !this.scrollable;
    },
    firstItemForActiveSideOwned() {
      return this.pricesGrouped.find((item) => {
        return (this.sideActive === 'right') ? item.sellOwned : item.buyOwned;
      });
    },
    showAllItemsNotice() {
      if (this.scrollable) return false;
      return this.pricesGrouped.length > this.showAllCut;
    },
    itemsToDisplay() {
      return this.showAll ? this.pricesGrouped : this.pricesGrouped.slice(0, this.showAllCut);
    },
    defaultDisplayRows() {
      if (this.usesStructuredRows) return [];

      const displayRows = [...this.itemsToDisplay];
      if (this.showInlineUnavailableSides && displayRows.length === 0 && (this.leftPricesUnavailable || this.rightPricesUnavailable)) {
        displayRows.push({
          index: 0,
          buyPrice: null,
          buyQuantity: null,
          buyOwned: false,
          sellPrice: null,
          sellQuantity: null,
          sellOwned: false,
        });
      }
      return displayRows;
    },
    displayRows() {
      let minimumRows = this.minimumRows;
      if (this.cardLayout && this.compactCardLayout) {
        minimumRows = this.isCardEmpty ? 1 : 0;
      }
      const displayRows = [...this.itemsToDisplay];
      const rowCount = Math.max(displayRows.length, minimumRows);

      while (displayRows.length < rowCount) {
        displayRows.push({
          index: displayRows.length,
          buyPrice: null,
          buyQuantity: null,
          buyOwned: false,
          sellPrice: null,
          sellQuantity: null,
          sellOwned: false,
        });
      }

      return displayRows;
    },
    isCardEmpty() {
      return this.cardLayout && this.pricesGrouped.length === 0;
    },
    isScrollableEmpty() {
      return this.scrollable && this.pricesGrouped.length === 0;
    },
    emptyOverlayText() {
      return 'New Orderbook.';
    },
  },
  methods: {
    ...mapActions('popup', ['setGenericSmallPopup']),
    ...mapActions('trade', ['getOrderBookTradeInfo', 'toggleTradeOperation']),
    getTradeType(operationType, operationValue) {
      if (operationType === 'buy' && operationValue === 'no') return 0;
      if (operationType === 'buy' && operationValue === 'yes') return 1;
      if (operationType === 'sell' && operationValue === 'no') return 2;
      if (operationType === 'sell' && operationValue === 'yes') return 3;
      return 1;
    },
    onMouseOver(index, side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = index;
    },
    onMouseLeave(side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = null;
    },
    sideClassModifiers(side) {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__side--active': this.sideActive === side,
        'order-book-sushi__side--inactive': this.sideActive !== side,
      };
    },
    shouldShowEmptyState(item, side, index) {
      if (this.isCardEmpty || this.isScrollableEmpty) return false;
      const isUnavailable = side === 'left' ? this.leftPricesUnavailable : this.rightPricesUnavailable;
      const priceKey = side === 'left' ? 'buyPrice' : 'sellPrice';
      return this.usesStructuredRows && index === 0 && isUnavailable && !item[priceKey];
    },
    shouldShowDefaultUnavailableState(side, index) {
      if (!this.showInlineUnavailableSides || index !== 0) return false;
      return side === 'left' ? this.leftPricesUnavailable : this.rightPricesUnavailable;
    },
    ownedTooltipText(owned) {
      return owned && this.showOwnedIndicators ? PRICE_LINKS_NOTICE : null;
    },
    hoverQuantityClassModifier(owned, index, side) {
      const isSideActive = this.sideActive === side && this.isHoverable;
      return {
        'order-book-sushi__body-cell--owned': owned,
        'order-book-sushi__body-cell--hovered': (isSideActive && this.hoveredIndex === index),
        'order-book-sushi__body-cell--clickable': isSideActive,
        'order-book-sushi__body-cell--inactive': !isSideActive,
        'order-book-prices-sushi__owned-tooltip': owned && this.showOwnedIndicators,
      };
    },
    hoverPriceClassModifier(owned, index, side) {
      const isSideActive = this.sideActive === side && this.isHoverable;
      return {
        'order-book-sushi__body-cell--price': true,
        'order-book-sushi__body-cell--hovered': (isSideActive && this.hoveredIndex === index),
        'order-book-sushi__body-cell--clickable': isSideActive,
        'order-book-sushi__body-cell--inactive': !isSideActive,
      };
    },
    showTradeInfo(item, operationType, operationValue, side) {
      const price = (side === 'right') ? item.sellPrice : item.buyPrice;
      const payload = {
        tradeType: this.getTradeType(operationType, operationValue),
        contractId: this.contract.contractId,
        price,
      };
      this.loaderAdd(document.body);
      this.getOrderBookTradeInfo(payload)
        .then((data) => {
          setTimeout(() => {
            this.loaderRemove(document.body);
            this.setGenericSmallPopup({
              name: MODALS.SMALL_OFFERS_INDICATOR,
              options: {
                items: data,
                operationType,
                price,
              },
            });
          }, 400);
        })
        .catch(() => this.loaderRemove(document.body));
    },
    handleClick(item, isMine, operationType, operationValue, side) {
      if (
        !isMine &&
        this.firstItemForActiveSideOwned &&
        item.index > this.firstItemForActiveSideOwned.index) {
        return;
      }
      if (isMine) {
        this.showTradeInfo(item, operationType, operationValue, side);
        return;
      }
      let quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
        return prev + curr.buyQuantity;
      }, 0);
      let price = this.pricesGrouped[item.index].buyPrice;

      if (side === 'right') {
        quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
          return prev + curr.sellQuantity;
        }, 0);
        price = this.pricesGrouped[item.index].sellPrice;
      }
      if (price === null) return;
      if (operationType === 'sell' && quantity > this.contract.userQuantity) {
        quantity = this.contract.userQuantity;
      }
      this.$emit('click', {
        quantity,
        price,
      });
    },
    onClick(item, isMine, side) {
      if (this.sideActive === side) {
        this.handleClick(item, isMine, this.operationType, this.operationValue, this.sideActive);
      } else if (isMine) {
        if (side === 'left') {
          this.showTradeInfo(item, this.operationTypeLeftText.toLowerCase(), this.operationValueText.toLowerCase(), side);
        } else {
          this.showTradeInfo(item, this.operationTypeRightText.toLowerCase(), this.operationValueRightText.toLowerCase(), side);
        }
      }
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
      ls.set(`order_book_prices_show_all_${this.contract.contractId}`, this.showAll);
    },
    updateShowAll() {
      if (this.scrollable) return;
      this.showAll = ls.get(`order_book_prices_show_all_${this.contract.contractId}`, false);
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-prices-sushi/order-book-prices-sushi.js